import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, combineLatest, filter, map, switchMap, takeUntil } from 'rxjs';

import { Account } from '../auth/models/account.model';
import { User } from '../auth/models/user.model';
import { LoginService } from '../auth/services/login.service';
import { Market } from '../company/modules/commercial/models/market.model';
import { Company } from '../models/company.model';
import { CompanyService } from '../services/company.service';
import { ConnectApplyService } from '../services/connect-apply.service';
import { isConnectReturnPending } from '../services/connect-apply.storage';
import { ComponentCommService } from '../services/component-comm.service';
import { DataDogLoggerService } from '../services/data-dog-logger.service';
import { SubscriptionManager } from '../utilities/subscription-manager';

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent extends SubscriptionManager implements OnInit, OnDestroy {

  public atLeastOneCompanyActive: boolean;
  public countDownValue: number = 0;
  public filterKey: string;
  public marketsPresent: Market[];
  public secondsToReSend: number = 0;
  /**
   * Flag used to enable/disable UI buttons and links when an API request is in
   * progress.
   */
  public processing: boolean;

  // Observables for async pipe
  public user$: Observable<User>;
  public accounts$: Observable<Account[]>;
  public viewData$: Observable<{ user: User; accounts: Account[] }>;

  /**
   * El usuario volvió de Agrology Connect (o tiene `#connect=1` pendiente) y el
   * apply está en curso: con 0 empresas se muestra "Trayendo tus empresas de
   * Agrology…" en vez de "registrá tu empresa". Para usuarios que no vienen de
   * Connect, el apply de arranque es silencioso y se ve el estado vacío normal.
   */
  public connectRunning: boolean = false;
  /** El apply lleva más de ~15 s: el spinner avisa que tarda más de lo normal. */
  public connectSlow: boolean = false;
  /**
   * El usuario volvió de Agrology Connect y no se pudieron traer (todas) sus
   * empresas. Con 0 empresas cambia el texto del estado vacío.
   */
  public syncFailed: boolean = false;
  /**
   * El usuario volvió de Agrology Connect pero no se sabe si ya se aplicó
   * (timeout/lock): aviso suave, no de error.
   */
  public syncPending: boolean = false;

  // Cached values for methods
  private currentUser: User;
  /** Última lista de accounts recibida (undefined = todavía ninguna). */
  private currentAccounts: Account[];
  /** En algún momento de esta visita la lista estuvo vacía (para el auto-ingreso 0 → 1 de Connect). */
  private sawZeroAccounts: boolean = false;
  /** Ya se auto-entró (o intentó) a una empresa: no volver a navegar por nuevas emisiones. */
  private autoEntered: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loginService: LoginService,
    private componentComm: ComponentCommService,
    private companyService: CompanyService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private dataDogLoggerService: DataDogLoggerService,
    private connectApplyService: ConnectApplyService
  ) {
    super();
    this.companyService.unset();
  }

  public set(company: Company): void {
    if (company.activation_level) {
      this.companyService.set(company);
      this.router.navigate(['/company', company.id, 'home']);
    }
  };

  ngOnInit(): void {
    this.componentComm.emit({ name: 'app-title', title: 'GLOBAL.MY_COMPANIES' });

    combineLatest([this.connectApplyService.applyState$, this.connectApplyService.progress$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([state, progress]) => {
        const pendingReturn = isConnectReturnPending();
        this.connectRunning = (progress.active && (progress.fromConnect || pendingReturn))
          || (state === 'idle' && pendingReturn);
        this.connectSlow = this.connectRunning && progress.slow;
      });

    this.connectApplyService.outcome$
      .pipe(takeUntil(this.destroy$))
      .subscribe(outcome => {
        this.syncFailed = !!outcome?.fromConnect &&
          (outcome.state === 'failed' || outcome.result?.failed?.length > 0);
        this.syncPending = !!outcome?.fromConnect && outcome.state === 'unknown';

        // Las accounts nuevas pueden haber llegado antes que la respuesta del
        // apply (p.ej. por Pusher): se evalúa también acá.
        if (this.currentAccounts?.length === 1 && this.connectJustAddedFirstCompany()) {
          this.autoEnter(this.currentAccounts[0].company);
        }
      });

    this.user$ = this.loginService.getCurrentUser().pipe(
      filter(user => !!user),
      takeUntil(this.destroy$)
    );

    this.accounts$ = this.user$.pipe(
      filter(user => !!user?.mail_verified),
      switchMap(() => this.loginService.watchAccounts()),
      filter(accounts => !!accounts),
      takeUntil(this.destroy$)
    );

    this.viewData$ = this.user$.pipe(
      switchMap(user =>
        this.loginService.watchAccounts().pipe(
          filter(accounts => accounts !== null && accounts !== undefined),
          map(accounts => {
            // Cache values
            this.currentUser = user;
            const firstEmission = this.currentAccounts === undefined;
            this.currentAccounts = accounts || [];
            if (this.currentAccounts.length === 0) this.sawZeroAccounts = true;

            // Process accounts
            if (accounts) {
              accounts.forEach(account => {
                if (account.company.activation_level && account.company.activation_level.id) {
                  this.atLeastOneCompanyActive = true;
                }

                const exists = this.marketsPresent?.find(m => m.id == account.company.market.id);
                if (!exists) {
                  this.marketsPresent = [...(this.marketsPresent || []), account.company.market];
                }
              });

              // Auto-navigate if single company: al venir del login, o si la
              // lista pasó de 0 a 1 porque Agrology Connect le acaba de dar su
              // primera empresa (y el usuario sigue en el listado). Una sola
              // vez: cualquier emisión posterior con 1 account no vuelve a navegar.
              const queryParams = this.route.snapshot.queryParams;
              const fromLogin = queryParams.referral == 'login' && firstEmission;

              if (!this.autoEntered && accounts.length == 1 && (fromLogin || this.connectJustAddedFirstCompany())) {
                this.autoEnter(accounts[0].company);
              } else {
                this.focus();
              }
            }

            // Check time to resend after user is loaded
            this.checkTimeToResend();

            return { user, accounts: accounts || [] };
          })
        )
      ),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Connect le dio al usuario su primera empresa en esta visita: la lista
   * estuvo vacía, el último apply sumó algo (o, si vino de Connect y no se
   * sabe el resultado por timeout/lock, llegó una empresa igual) y el usuario
   * sigue en el listado.
   */
  private connectJustAddedFirstCompany(): boolean {
    const outcome = this.connectApplyService.lastOutcome;
    const added = !!outcome?.addedCompanies || (!!outcome?.fromConnect && outcome.state === 'unknown');

    return this.sawZeroAccounts && added && this.router.url.split(/[?#]/)[0] === '/companies';
  }

  private autoEnter(company: Company): void {
    if (this.autoEntered) return;
    this.autoEntered = true;
    this.set(company);
  }

  public sendConfirmationEmail(): void {
    if (this.currentUser.mail_verified) return;

    this.processing = true;
    this.secondsToReSend = 0;

    this.loginService.resendVerification(this.currentUser.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.secondsToReSend = res.data.retryAfter;
          this.toastrService.success(this.translateService.instant('EMAIL.EMAIL_SEND_SUCCESS'));
          this.processing = false;
        },
        error: error => {
          this.secondsToReSend = error.data.retryAfter;
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  /** If user's email si not verified, check the waiting time to resend verification. */
  private checkTimeToResend(): void {
    if (!this.currentUser || this.currentUser.mail_verified || this.processing || this.secondsToReSend > 0) return;

    this.processing = true;

    this.loginService.getVerificationRemainingTimeToTry(this.currentUser.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.secondsToReSend = res.data.retryAfter;
          this.processing = false;
        },
        error: error => {
          this.secondsToReSend = error.data.retryAfter;
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  private focus(delay: number = 0): void {
    setTimeout(() => {
      const input = document.getElementById('filterKey');
      if (input) input.focus();
    }, delay);
  }

  /** @ignore */
  ngOnDestroy(): void {
    // Unsubscribe from everything
    super.ngOnDestroy();
  }
}
